'use strict';

import axios from 'axios';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  toggleClassToElement
} from '@jspartials/class-list-utils';
import { pushOnDataLayer } from '@jspartials/gtm-utils';
import {
  createElement,
  appendElementContent,
  listenDOMEvent
} from '@jspartials/dom-api-utils';
import {
  html,
  body,
  setHtmlUnscrollable,
  setHtmlScrollable
} from '@jspartials/general-utils';
import { addToCartItem } from '@jspartials/cart-utils';

const selector = {
  vto: {
    id: 'vto',
    class: '.js-virtual-try-on',
    button: {
      open: '.js-virtual-try-on-open-button',
      close: '.js-virtual-try-on-close-button',
      getProducts: '.js-virtual-try-on__button-get-products',
      navigationCarousel: '.js-virtual-try-on__button-nav-carousel',
      addToCart: '.js-virtual-try-on__button-add-to-cart',
      genderButton: '.js-virtual-try-on__gender-button'
    },
    moreProducts: {
      wrapper: '.js-virtual-try-on__more-products-wrapper',
      class: '.js-virtual-try-on__more-products',
      button: '.js-virtual-try-on__button-load-more-products'
    },
    preferences: {
      class: '.js-virtual-try-on-preferences',
      button: '.js-virtual-try-on__button-open-preferences'
    },
    product: {
      name: '.js-virtual-try-on__product-name',
      priceSales: '.js-virtual-try-on__product-price-sales',
      priceList: '.js-virtual-try-on__product-price-list',
      thumbnailClass: 'js-virtual-try-on__product-thumbnail'
    },
    dipSelect: '.js-virtual-try-on__dip-select'
  }
};

// Events
const eventNameToReloadVtoButtons = 'reloadVtoButtons';
const gtmEventName = 'vtoEvent';
const gtmEventCategory = 'vto';
const productSelectOptionsModal = 'product:selectOptionsModal';

// Elements
let virtualTryOnWrapper = null;
let virtualTryOnMoreProductsWrapper = null;
let virtualTryOnMoreProducts = null;
let virtualTryOnUrlToGetProducts = null;
let virtualTryOnMainScreen = null;
let virtualTryOnPreferences = null;
let vtoOpenPreferencesButton = null;
const virtualTryOnOpenButtonsObserved = [];

// Variables
let myFitmixWidget = null;
const virtualTryOnLanguage = html.getAttribute('lang');
const params = {
  lang: virtualTryOnLanguage
};

let virtualTryOnUrlScriptLoaded = false;
let currentPaginationPage = 1;
let currentProductId = null;
let htmlLoadMoreButton = '';
let buttonToLoadMoreProducts = null;
let openedAfterLoadPage = false;

// Functions

/**
 * Adding the script tag to the head
 * @param {String} url - The url of the script
 * @param {Function} callback - The callback function
 */
function loadScript(url, callback) {
  var head = document.head;
  var script = document.createElement('script');

  script.type = 'text/javascript';
  script.src = url;
  script.onreadystatechange = callback;
  script.onload = callback;
  virtualTryOnUrlScriptLoaded = true;
  head.appendChild(script);
}

/**
 * Set the frame
 * @param {String} frame - The frame to open
 */
const selectFrame = function (frame) {
  if (!myFitmixWidget) {
    // create fitmix only once
    params.frame = frame;
  } else {
    myFitmixWidget.setFrame(frame);
  }
};

const createWidgetAndStartVto = () => {
  if (!myFitmixWidget) {
    // create fitmix only once
    myFitmixWidget = FitMix.createWidget(selector.vto.id, params, function () {
      myFitmixWidget.startVto('live');
    });
  } else {
    myFitmixWidget.startVto('live');
  }
};

/**
 * Open the virtual try on
 * @param {HTMLElement} button - The button that has been clicked
 */
const openVto = (button) => {
  const url = button.dataset.url;
  axios
    .post(url)
    .then(function (response) {
      if (response.status === 200) {
        const responseData = response.data;
        virtualTryOnUrlToGetProducts = responseData.vtoUrlGetProducts;
        currentProductId = responseData.vtoFrame;
        htmlLoadMoreButton = responseData.vtoHtmlCodeForLoadMoreButton;
        initVirtualTryOn(
          responseData.vtoUrlScript,
          responseData.vtoApiKey,
          responseData.vtoHtmlCode
        );
      } else {
        console.error('Error trying open the virtual try on');
      }
    })
    .catch(function () {
      console.log('ERROR! Cannot open the virtual try on');
    });
};

/**
 * Close the VTO
 */
const closeVirtualTryOn = () => {
  myFitmixWidget.stopVto(); // -> this stop the stream but keep the camera opened
  // myFitmixWidget.closeCamera(); // -> this stop the camera completely
  addClassToElement(virtualTryOnWrapper, classes.hidden);
  virtualTryOnMoreProducts.innerHTML = '';
  currentPaginationPage = 1;
  currentProductId = null;
  setHtmlScrollable();
};

/**
 * Init the virtual try on
 * @param {String} urlScript - The url of the script to load
 * @param {String} apiKey - The api key to use for the virtual try on
 * @param {String} htmlCode - The html code to use for the virtual try on window
 */
const initVirtualTryOn = (urlScript, apiKey, htmlCode) => {
  params.apiKey = apiKey;
  if (!virtualTryOnUrlScriptLoaded) {
    loadScript(urlScript, () => {
      showVirtualTryOn(htmlCode);
      selectFrame(currentProductId);
      createWidgetAndStartVto();
    });
  } else {
    showVirtualTryOn(htmlCode);
    selectFrame(currentProductId);
    createWidgetAndStartVto();
  }
};

/**
 * Show the virtual try on window
 * @param {String} html - The html code to show
 * @returns {void}
 */
const showVirtualTryOn = (html) => {
  setHtmlUnscrollable();

  if (virtualTryOnWrapper) {
    removeClassToElement(virtualTryOnWrapper, classes.hidden);
  } else {
    virtualTryOnWrapper = createElement({
      tag: 'div',
      content: html
    });
    virtualTryOnMoreProductsWrapper = virtualTryOnWrapper.querySelector(
      selector.vto.moreProducts.wrapper
    );
    virtualTryOnMoreProducts = virtualTryOnWrapper.querySelector(
      selector.vto.moreProducts.class
    );
    virtualTryOnMainScreen = virtualTryOnWrapper.querySelector(
      selector.vto.class
    );
    virtualTryOnPreferences = virtualTryOnWrapper.querySelector(
      selector.vto.preferences.class
    );
    appendElementContent(body, virtualTryOnWrapper);
    listenVirtualTryOnCloseButton();
    listenVirtualTryOnOpenPreferencesButton();
    listenVirtualTryOnGetProductsButton();
    listenVirtualTryOnNavigationCarouselButtons();
    listenVirtualTryOnGenderButtons();
    listenVirtualTryOnDipSelect();
    listenVirtualTryOnAddToCartButton();
  }

  getProducts();
};

/**
 * Close the VTO preferences
 */
const closeVtoPreferences = () => {
  removeClassToElement(virtualTryOnMainScreen, classes.hidden);
  removeClassToElement(vtoOpenPreferencesButton, classes.hidden);
  addClassToElement(virtualTryOnPreferences, classes.hidden);
};

/**
 * Open the VTO preferences
 */
const openVtoPreferences = () => {
  removeClassToElement(virtualTryOnPreferences, classes.hidden);
  addClassToElement(virtualTryOnMainScreen, classes.hidden);
  addClassToElement(vtoOpenPreferencesButton, classes.hidden);
};

/**
 * Set the pupillary distance between the eyes
 * @param {Integer} distance - The distance between pupiles
 * @returns {void}
 */
const setDIP = (distance) => {
  if (myFitmixWidget) {
    myFitmixWidget.setPupillaryDistance(distance);
  }
  closeVtoPreferences();
};

/**
 * Set the gender to use for the virtual try on
 * @param {String} genderCode - The gender code to use
 * @returns {void}
 */
const setGender = (genderCode) => {
  if (myFitmixWidget) {
    myFitmixWidget.setUserInfo({
      gender: genderCode
    });
  }
  closeVtoPreferences();
};

/**
 * Listen the open buttons of the virtual try on
 */
const listenVirtualTryOnOpenButtons = () => {
  const vtoOpenButtons = document.querySelectorAll(selector.vto.button.open);

  // Open the VTO
  vtoOpenButtons.forEach((vtoOpenButton) => {
    if (!isObserved(vtoOpenButton)) {
      addToObservedElements(vtoOpenButton);
      openVtoAfterLoadPage(vtoOpenButton);

      listenDOMEvent(vtoOpenButton, 'click', function () {
        openVto(vtoOpenButton);
        launchGtmEventClickVtoButton(vtoOpenButton);
      });
    }
  });
};

/**
 * Listen the close button of the virtual try on
 */
const listenVirtualTryOnCloseButton = () => {
  const vtoCloseButton = virtualTryOnWrapper.querySelector(
    selector.vto.button.close
  );

  listenDOMEvent(vtoCloseButton, 'click', () => {
    closeVirtualTryOn();
  });
};

/**
 * Listen the open preferences button of the virtual try on
 */
const listenVirtualTryOnOpenPreferencesButton = () => {
  vtoOpenPreferencesButton = virtualTryOnWrapper.querySelector(
    selector.vto.preferences.button
  );

  listenDOMEvent(vtoOpenPreferencesButton, 'click', openVtoPreferences);
};

/**
 * Listen the get products button of the virtual try on
 */
const listenVirtualTryOnGetProductsButton = () => {
  const vtoGetProductsButton = virtualTryOnWrapper.querySelector(
    selector.vto.button.getProducts
  );

  listenDOMEvent(vtoGetProductsButton, 'click', () => {
    toggleClassToElement(virtualTryOnMoreProductsWrapper, classes.is.open);
    toggleClassToElement(vtoOpenPreferencesButton, classes.hidden);
  });
};

/**
 * Listen the navigation carousel buttons of the virtual try on
 */
const listenVirtualTryOnNavigationCarouselButtons = () => {
  const vtoNavigationCarouselButtons = virtualTryOnWrapper.querySelectorAll(
    selector.vto.button.navigationCarousel
  );

  vtoNavigationCarouselButtons.forEach((vtoNavigationCarouselButton) => {
    listenDOMEvent(vtoNavigationCarouselButton, 'click', () => {
      const vtoNavigationCarouselButtonDirection =
        vtoNavigationCarouselButton.dataset.direction;
      const distance =
        vtoNavigationCarouselButtonDirection === 'left' ? -600 : 600;
      virtualTryOnMoreProducts.scrollBy({
        top: 0,
        left: distance,
        behavior: 'smooth'
      });
    });
  });
};

/**
 * Listen the gender buttons of the virtual try on
 */
const listenVirtualTryOnGenderButtons = () => {
  const vtoGenderButtons = virtualTryOnWrapper.querySelectorAll(
    selector.vto.button.genderButton
  );

  vtoGenderButtons.forEach((vtoGenderButton) => {
    listenDOMEvent(vtoGenderButton, 'click', () => {
      const genderCode = vtoGenderButton.value;
      setGender(genderCode);
    });
  });
};

/**
 * Listen the dip select of the virtual try on
 */
const listenVirtualTryOnDipSelect = () => {
  const vtoDipSelect = virtualTryOnWrapper.querySelector(
    selector.vto.dipSelect
  );

  listenDOMEvent(vtoDipSelect, 'change', () => {
    const dip = parseInt(vtoDipSelect.value);
    setDIP(dip);
  });
};

/**
 * Listen the add to cart button of the virtual try on
 */
const listenVirtualTryOnAddToCartButton = () => {
  const vtoAddToCartButton = virtualTryOnWrapper.querySelector(
    selector.vto.button.addToCart
  );

  listenDOMEvent(vtoAddToCartButton, 'click', () => {
    const productId = vtoAddToCartButton.dataset.pid;
    const isGlasses = JSON.parse(vtoAddToCartButton.dataset.glasses);
    const url = vtoAddToCartButton.dataset.url;
    const urlBase = window.location.protocol + '//' + window.location.hostname;
    const urlToRedirect = new URL(url, urlBase);
    const from = vtoAddToCartButton.dataset.from;
    urlToRedirect.searchParams.append('showProductOptionsModal', 'true');

    if (isGlasses) {
      if (from === 'plp') {
        window.location.href = urlToRedirect;
      }

      if (from === 'pdp') {
        closeVirtualTryOn();
        body.dispatchEvent(new Event(productSelectOptionsModal));
      }
    } else {
      addClassToElement(vtoAddToCartButton, classes.is.loading);

      addToCartItem(productId, 1, 'vto', [])
        .then(() => addClassToElement(vtoAddToCartButton, classes.has.success))
        .catch((error) => {
          if (error.response.status === 409) {
            addClassToElement(vtoAddToCartButton, classes.is.full);
          } else {
            addClassToElement(vtoAddToCartButton, classes.has.error);
          }
        })
        .then(() => {
          removeClassToElement(vtoAddToCartButton, classes.is.loading);
          setTimeout(() => {
            removeClassToElement(vtoAddToCartButton, classes.has.success);
            removeClassToElement(vtoAddToCartButton, classes.is.full);
            removeClassToElement(vtoAddToCartButton, classes.has.error);
          }, 4000);
        });
    }
  });
};

/**
 * Obtain the products to show in the virtual try on
 * @returns {void}
 */
const getProducts = () => {
  const formData = new FormData();

  formData.append('productId', currentProductId);
  formData.append('currentPaginationPage', currentPaginationPage);

  if (currentPaginationPage === 1) {
    addClassToElement(virtualTryOnMoreProductsWrapper, classes.is.loading);
  } else {
    addClassToElement(buttonToLoadMoreProducts, classes.is.loading);
  }

  axios
    .post(virtualTryOnUrlToGetProducts, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(function (response) {
      if (response.status === 200) {
        const responseData = response.data;
        const products = responseData.products || [];
        const moreProducts = responseData.moreProducts || false;
        paintProducts(products, moreProducts);
      }
    })
    .catch(function () {
      console.error('ERROR! Cannot get products with virtual try on');
    })
    .then(function () {
      removeClassToElement(virtualTryOnMoreProductsWrapper, classes.is.loading);
      removeClassToElement(buttonToLoadMoreProducts, classes.is.loading);
    });
};

/**
 * Paint the products in the virtual try on
 * @param {Array} products - The products to paint
 * @param {Boolean} moreProducts - If there are more products to show
 * @returns {void}
 */
const paintProducts = (products, moreProducts) => {
  deleteButtonToLoadMoreProducts();

  if (products.length > 0) {
    products.forEach((product, index) => {
      const productImageElement = createElement({
        tag: 'img',
        attributes: [
          {
            name: 'src',
            value: product.image
          }
        ]
      });
      const productElement = createElement({
        tag: 'div',
        content: productImageElement,
        classes: [
          'tw-relative',
          'tw-w-1/3',
          'tw-border',
          'tw-border-white',
          'tw-border-solid',
          'md:tw-inline-block',
          'md:tw-w-1/5',
          'md:tw-snap-center',
          'hw-virtual-try-on__product-thumbnail',
          selector.vto.product.thumbnail
        ],
        attributes: [
          {
            name: 'data-frame',
            value: product.id
          },
          {
            name: 'data-name',
            value: product.name
          }
        ]
      });

      if (index === 0 && currentPaginationPage === 1) {
        addClassToElement(productElement, classes.is.selected);
      }

      appendElementContent(virtualTryOnMoreProducts, productElement);

      listenDOMEvent(productElement, 'click', () => {
        markCurrentProductAsSelected(productElement);
        updateVirtualTryOnProduct(product);
        removeClassToElement(virtualTryOnMoreProductsWrapper, classes.is.open);
        removeClassToElement(vtoOpenPreferencesButton, classes.hidden);
      });
    });
  }

  if (moreProducts) {
    paintButtonToLoadMoreProducts();

    currentPaginationPage++;
  }
};

/**
 * Paint the button to load more products
 * @returns {void}
 */
const paintButtonToLoadMoreProducts = () => {
  buttonToLoadMoreProducts = createElement({
    tag: 'div',
    content: htmlLoadMoreButton,
    classes: [
      'tw-relative',
      'tw-w-1/3',
      'tw-text-center',
      'tw-whitespace-normal',
      'tw-border',
      'tw-border-white',
      'tw-border-solid',
      'tw-cursor-pointer',
      'md:tw-inline-block',
      'md:tw-w-1/5',
      'md:tw-snap-center',
      'hw-virtual-try-on-load-more-button',
      selector.vto.moreProducts.button
    ]
  });

  appendElementContent(virtualTryOnMoreProducts, buttonToLoadMoreProducts);

  listenDOMEvent(buttonToLoadMoreProducts, 'click', () => {
    getProducts();
  });
};

/**
 * Delete the button to load more products
 * @returns {void}
 */
const deleteButtonToLoadMoreProducts = () => {
  if (buttonToLoadMoreProducts) {
    buttonToLoadMoreProducts.remove();
  }
};

/**
 * Mark the current product as selected
 * @param {HTMLElement} element - The element to mark as selected
 * @returns {void}
 */
const markCurrentProductAsSelected = (element) => {
  const currentSelectedProductThumbnail = virtualTryOnWrapper.querySelector(
    `.${selector.vto.product.thumbnail}.${classes.is.selected}`
  );

  if (currentSelectedProductThumbnail) {
    removeClassToElement(currentSelectedProductThumbnail, classes.is.selected);
  }
  addClassToElement(element, classes.is.selected);
};

/**
 * Update the virtual try on with the new product
 * @param {Object} product - The product to update
 * @returns {void}
 */
const updateVirtualTryOnProduct = (product) => {
  updateProductNames(product.name);
  updateProductPrice(product.price);
  updateProductId(product.id);
  updateProductUrl(product.url);
  selectFrame(product.id);
};
/**
 * Update the product names in the virtual try on
 * @param {String} productName - The product name to update
 * @returns {void}
 */
const updateProductNames = (productName) => {
  const productNames = virtualTryOnWrapper.querySelectorAll(
    selector.vto.product.name
  );
  productNames.forEach((productNameElement) => {
    productNameElement.innerHTML = productName;
  });
};

/**
 * Update the product price in the virtual try on
 * @param {Object} price - The price to update
 * @returns {void}
 */
const updateProductPrice = (price) => {
  const productPriceList = virtualTryOnWrapper.querySelector(
    selector.vto.product.priceList
  );
  const productPriceSales = virtualTryOnWrapper.querySelector(
    selector.vto.product.priceSales
  );

  if (productPriceList && productPriceSales) {
    if (price.list) {
      productPriceList.innerHTML = price.list.formatted;
      removeClassToElement(productPriceList, classes.hidden);
    } else {
      addClassToElement(productPriceList, classes.hidden);
    }

    productPriceSales.innerHTML = price.sales.formatted;
  }
};

/**
 * Update the product id in the virtual try on add to cart button
 * @param {String} productId - The product id to update
 * @returns {void}
 */
const updateProductId = (productId) => {
  const addToCartButton = virtualTryOnWrapper.querySelector(
    selector.vto.button.addToCart
  );
  addToCartButton.dataset.pid = productId;
};

/**
 * Update the product url in the virtual try on add to cart button
 * @param {String} productUrl - The product url to update
 * @returns {void}
 */
const updateProductUrl = (productUrl) => {
  const addToCartButton = virtualTryOnWrapper.querySelector(
    selector.vto.button.addToCart
  );
  addToCartButton.dataset.url = productUrl;
};

/**
 * GTM event to track the click on the virtual try on open buttons
 * @param {HTMLElement} element - Virtual try on open button
 * @returns {void}
 */
const launchGtmEventClickVtoButton = (element) => {
  const eventAction = element.dataset.gtmEventActionClick;
  const eventLabel = element.dataset.frame;

  pushOnDataLayer({
    event: gtmEventName,
    category: gtmEventCategory,
    action: eventAction,
    label: eventLabel
  });
};

/**
 * Determine if the element is already observed
 * @param {HTMLElement} element - The element to check
 * @returns {Boolean} - True if the element is already observed
 */
const isObserved = (element) => {
  return virtualTryOnOpenButtonsObserved.includes(element);
};

/**
 * Add the element to the list of observed elements
 * @param {HTMLElement} element - The element to add to the observed elements
 */
const addToObservedElements = (element) => {
  virtualTryOnOpenButtonsObserved.push(element);
};

/**
 * Open the virtual try on after the page is loaded
 * @param {HTMLElement} button - The virtual try on open button
 * @returns {void}
 */
const openVtoAfterLoadPage = (button) => {
  const openVtoAfterLoadPage = button.dataset.openAfterLoadPage
    ? JSON.parse(button.dataset.openAfterLoadPage)
    : false;

  if (openVtoAfterLoadPage && !openedAfterLoadPage) {
    openVto(button);
    openedAfterLoadPage = true;
  }
};

// Init
document.addEventListener('DOMContentLoaded', function () {
  listenVirtualTryOnOpenButtons();

  document.addEventListener(eventNameToReloadVtoButtons, () => {
    listenVirtualTryOnOpenButtons();
  });
});
